import type { ValuesByName } from "@/types/visibility";

const RESPONSE_DRAFT_PREFIX = "chmforms:response-draft:v1:";

type StoredResponseDraft = {
  version: 1;
  values: ValuesByName;
  currentStep: number;
};

export type FormResponseDraft = Pick<
  StoredResponseDraft,
  "values" | "currentStep"
>;

export function formResponseDraftKey(formId: string | number): string {
  return `${RESPONSE_DRAFT_PREFIX}${formId}`;
}

function validFormIdentifier(formId: string | number): boolean {
  if (typeof formId === "number") return Number.isFinite(formId);
  return String(formId).trim() !== "";
}

function browserStorage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function removeStoredDraft(storage: Storage, key: string): void {
  try {
    storage.removeItem(key);
  } catch {
    // Storage can be blocked independently from access to window.localStorage.
  }
}

export function loadFormResponseDraft(
  formId: string | number,
  fieldNames: Iterable<string>,
): FormResponseDraft | null {
  const storage = browserStorage();
  if (!storage || !validFormIdentifier(formId)) return null;

  const key = formResponseDraftKey(formId);
  try {
    const raw = storage.getItem(key);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<StoredResponseDraft>;
    if (
      parsed.version !== 1 ||
      !parsed.values ||
      typeof parsed.values !== "object" ||
      Array.isArray(parsed.values)
    ) {
      removeStoredDraft(storage, key);
      return null;
    }

    const allowedNames = new Set(fieldNames);
    const values = Object.fromEntries(
      Object.entries(parsed.values).filter(
        ([name, value]) => allowedNames.has(name) && typeof value === "string",
      ),
    );
    const currentStep = Number.isInteger(parsed.currentStep)
      ? Math.max(0, Number(parsed.currentStep))
      : 0;

    return { values, currentStep };
  } catch {
    removeStoredDraft(storage, key);
    return null;
  }
}

export function saveFormResponseDraft(
  formId: string | number,
  draft: FormResponseDraft,
): void {
  const storage = browserStorage();
  if (!storage || !validFormIdentifier(formId)) return;

  const values = Object.fromEntries(
    Object.entries(draft.values).filter(
      ([, value]) => typeof value === "string" && value !== "",
    ),
  );
  const currentStep = Math.max(0, Math.floor(draft.currentStep));
  const key = formResponseDraftKey(formId);

  try {
    if (Object.keys(values).length === 0 && currentStep === 0) {
      removeStoredDraft(storage, key);
      return;
    }

    const stored: StoredResponseDraft = {
      version: 1,
      values,
      currentStep,
    };
    storage.setItem(key, JSON.stringify(stored));
  } catch {
    // The form must remain usable when browser storage is unavailable or full.
  }
}

export function clearFormResponseDraft(formId: string | number): void {
  const storage = browserStorage();
  if (!storage || !validFormIdentifier(formId)) return;

  removeStoredDraft(storage, formResponseDraftKey(formId));
}
